import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import toastr from 'toastr';
import { useTranslation } from 'react-i18next';
import {
  Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader,
} from 'reactstrap';
import { get, post } from '../../helpers/api_helper';

const returnSkus = ['Outside', 'Inside'].flatMap((orientation) => [
  `OG_${orientation}_RED-NO`,
  `OG_${orientation}_RED-YES`,
  `Kermit_${orientation}_RED-YES`,
  `Kermit_${orientation}_RED-NO`,
  `Flag_${orientation}_RED-YES`,
  `NoHousing_${orientation}_RED-YES`,
  `NoHousing_${orientation}_RED-NO`,
]);

export function getFamilyReturnLot(date = new Date()) {
  return `CRXX${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}01`;
}

export default function FamilyReturnLabelModal({ saleItem, onClose }) {
  const { t } = useTranslation();
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [printedLot, setPrintedLot] = useState('');
  const pending = useRef(false);
  const recordedInduction = useRef(null);

  const print = async (sku) => {
    if (!sku || pending.current || printedLot) return;
    pending.current = true;
    setSelected(sku);
    setPrinting(true);
    setPrintedLot('');
    setError('');
    try {
      if (!recordedInduction.current) {
        const items = await post('/api/items/pns', { part_numbers: [sku] });
        const matches = Array.isArray(items)
          ? items.filter((item) => item.part_number === sku && item.id) : [];
        if (matches.length !== 1) {
          throw new Error(t('The selected SKU must match exactly one item before it can be inducted.'));
        }
        const item = matches[0];
        const inventory = await get(`/api/burnsville/inventoryByItemId/${item.id}`);
        if (!inventory?.stock_id || !inventory?.part_number) {
          throw new Error(t('The selected component has no printable warehouse stock record.'));
        }
        const lot = getFamilyReturnLot();
        const orders = await get('/api/sourcing/orders', {
          params: { f: `o.lot=${lot}`, page: 1, per: 2 },
        });
        if (Number(orders?.total) !== 1 || orders?.results?.length !== 1
          || !orders.results[0].id || orders.results[0].lot !== lot) {
          throw new Error(t('Expected exactly one sourcing order for lot {{lot}}. Create or correct the monthly order before printing.', { lot }));
        }
        await post(`/api/sourcing/orders/induct/${orders.results[0].id}`, {
          item_id: item.id,
          qty: 1,
        });
        // Keep the recorded induction and its lot when retrying a failed print.
        recordedInduction.current = { inventory, lot };
      }
      const { inventory, lot } = recordedInduction.current;
      await post(`/api/burnsville/printDZLabel/${encodeURIComponent(inventory.stock_id)}?qty=1`, {
        lot,
        part_number: inventory.part_number,
      });
      setPrintedLot(lot);
      onClose();
      // A failed audit must not report the already-printed label as a print failure.
      try {
        await post('/api/user/actions/create', {
          action: 'printFamilyReturn', entity: 'saleItem', entityId: saleItem.id,
        });
      } catch (err) {
        toastr.warning(t('Label printed, but the action could not be logged.'));
      }
    } catch (err) {
      const detail = err.response?.data?.error || err.message;
      setError(recordedInduction.current
        ? `${t('Return recorded in sourcing History, but the label could not be printed. Retry printing to use the same induction.')} ${detail}`
        : detail);
    } finally {
      pending.current = false;
      setPrinting(false);
    }
  };

  return (
    <Modal isOpen size="lg" toggle={printing ? undefined : onClose} backdrop={printing ? 'static' : true}>
      <ModalHeader toggle={printing ? undefined : onClose}>{t('Print Family Return Label')}</ModalHeader>
      <ModalBody>
        <p>{t('Select a SKU to record the return and print its shelf label using the current month’s CRXX lot. This popup will close after printing.')}</p>
        {error && <Alert color="danger">{error}</Alert>}
        {printedLot && <Alert color="success">{t('Shelf label printed with lot {{lot}}.', { lot: printedLot })}</Alert>}
        {['Outside', 'Inside'].map((orientation) => (
          <fieldset key={orientation} disabled={printing} className="border rounded p-3 mb-3">
            <legend className="fs-6">{t(orientation)}</legend>
            <div className="d-flex flex-wrap gap-2">
              {returnSkus.filter((sku) => sku.includes(`_${orientation}_`)).map((sku) => (
                <label key={sku} htmlFor={`return-${sku}`} className={`border rounded p-2 ${selected === sku ? 'border-primary' : ''}`}>
                  <input
                    id={`return-${sku}`}
                    type="radio"
                    name="family-return-component"
                    className="form-check-input me-2"
                    checked={selected === sku}
                    disabled={!!recordedInduction.current && !printedLot}
                    onChange={() => print(sku)}
                  />
                  {sku}
                </label>
              ))}
            </div>
          </fieldset>
        ))}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" disabled={printing} onClick={onClose}>{t('Close')}</Button>
        {printing && <span role="status">{t('Printing...')}</span>}
        {error && (
          <Button color="primary" disabled={printing || !!printedLot} onClick={() => print(selected)}>
            {t('Retry Print Shelf Label')}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}

FamilyReturnLabelModal.propTypes = {
  saleItem: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};
